import React from 'react';
import { Form } from 'react-bootstrap';
import CountrySearchSelect from '../../common/CountrySearchSelect';
import { useCountries } from '../../../hooks/useCountries';
import {
  LOCATION_OPTIONS,
  LOCATION_SPECIFIC_COUNTRY,
  SALARY_CURRENCIES,
  SALARY_PERIODS,
} from './wizardConstants';
import { useJobWizard } from './JobWizardContext';

function locationRadioTestId(value) {
  return `rwm-wizard-2-location-${value.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '')}`;
}

export default function Step2SalaryDetails() {
  const { form, stepErrors: errors, updateForm: onChange } = useJobWizard();
  const { countries, loading } = useCountries();

  const locationMode = form.location_country
    ? LOCATION_SPECIFIC_COUNTRY
    : form.location_eligibility || '';

  const selectRegion = (value) => {
    onChange('location_eligibility', value);
    onChange('location_country', '');
  };

  const selectSpecificCountryMode = () => {
    onChange('location_eligibility', '');
  };

  const selectCountry = (name) => {
    onChange('location_country', name);
    onChange('location_eligibility', '');
  };

  const locationInvalid = Boolean(errors.location_eligibility || errors.location_country);

  return (
    <div className="rwm-wizard-step">
      <h2 className="rwm-wizard-step__title">Salary &amp; Details</h2>
      <p className="rwm-wizard-step__intro text-muted">
        Salary is always shown on your listing. Remote eligibility and timezone notes help candidates
        self-qualify.
      </p>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Form.Group>
            <Form.Label htmlFor="rwm-wizard-2-salary-min">Minimum salary</Form.Label>
            <Form.Control
              id="rwm-wizard-2-salary-min"
              type="number"
              min="0"
              step="1"
              value={form.salary_min}
              onChange={(e) => onChange('salary_min', e.target.value)}
              isInvalid={Boolean(errors.salary_min)}
              data-testid="rwm-wizard-2-salary-min"
            />
            <Form.Control.Feedback type="invalid">{errors.salary_min}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label htmlFor="rwm-wizard-2-salary-max">Maximum salary</Form.Label>
            <Form.Control
              id="rwm-wizard-2-salary-max"
              type="number"
              min="0"
              step="1"
              value={form.salary_max}
              onChange={(e) => onChange('salary_max', e.target.value)}
              isInvalid={Boolean(errors.salary_max)}
              data-testid="rwm-wizard-2-salary-max"
            />
            <Form.Control.Feedback type="invalid">{errors.salary_max}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="col-md-2">
          <Form.Group>
            <Form.Label htmlFor="rwm-wizard-2-currency">Currency</Form.Label>
            <Form.Select
              id="rwm-wizard-2-currency"
              value={form.salary_currency}
              onChange={(e) => onChange('salary_currency', e.target.value)}
              data-testid="rwm-wizard-2-currency"
            >
              {SALARY_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-md-2">
          <Form.Group>
            <Form.Label htmlFor="rwm-wizard-2-period">Period</Form.Label>
            <Form.Select
              id="rwm-wizard-2-period"
              value={form.salary_period}
              onChange={(e) => onChange('salary_period', e.target.value)}
              data-testid="rwm-wizard-2-period"
            >
              {SALARY_PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      <Form.Group className="mb-3">
        <Form.Label>Hiring remotely from</Form.Label>
        <div
          className="d-flex flex-column gap-1"
          data-testid="rwm-wizard-2-location-region"
          role="radiogroup"
          aria-label="Hiring remotely from"
        >
          {LOCATION_OPTIONS.map((loc) => (
            <Form.Check
              key={loc}
              type="radio"
              name="rwm-wizard-2-location"
              id={`rwm-wizard-2-location-${loc}`}
              label={loc}
              value={loc}
              checked={locationMode === loc}
              onChange={() => selectRegion(loc)}
              data-testid={locationRadioTestId(loc)}
            />
          ))}
          <Form.Check
            type="radio"
            name="rwm-wizard-2-location"
            id="rwm-wizard-2-location-specific-country"
            label="Specific country"
            value={LOCATION_SPECIFIC_COUNTRY}
            checked={locationMode === LOCATION_SPECIFIC_COUNTRY}
            onChange={selectSpecificCountryMode}
            data-testid="rwm-wizard-2-location-specific-country"
          />
        </div>
        {locationMode === LOCATION_SPECIFIC_COUNTRY ? (
          <div className="mt-2">
            <CountrySearchSelect
              countries={countries}
              loading={loading}
              value={form.location_country}
              onChange={selectCountry}
              invalid={locationInvalid}
            />
          </div>
        ) : null}
        {errors.location_eligibility ? (
          <div className="rwm-auth__field-error mt-2">{errors.location_eligibility}</div>
        ) : null}
      </Form.Group>

      <Form.Group className="mb-0">
        <Form.Label htmlFor="rwm-wizard-2-timezone">Timezone requirements (optional)</Form.Label>
        <Form.Control
          id="rwm-wizard-2-timezone"
          type="text"
          maxLength={100}
          value={form.timezone_requirement}
          onChange={(e) => onChange('timezone_requirement', e.target.value)}
          placeholder="e.g. Overlap with CET 10:00–16:00"
          data-testid="rwm-wizard-2-timezone"
        />
      </Form.Group>
    </div>
  );
}
